import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "联系新御宅屋",
  description: "联系新御宅屋：投稿安利、提交避雷建议、合作交流。",
};

const contactItems = [
  { icon: "ri-mail-line", title: "邮箱联系", value: "hello@xinyuzhaiwu.example.com", hint: "投稿、合作、建议" },
  { icon: "ri-wechat-line", title: "官方交流群", value: "关注首页同好入口", hint: "进群和同好畅聊" },
  { icon: "ri-time-line", title: "回复时效", value: "1-3 个工作日", hint: "我们会尽快回复你" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get("website_alt") ?? "").toString().trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    const body = new URLSearchParams();
    fd.forEach((value, key) => body.append(key, value.toString()));

    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch("https://readdy.ai/api/form/da1c64lrlbpoffc5h3ug", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, any> = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText;
      const lower = (serverMsg || "").toLowerCase();
      if (res.ok && parsed?.code === "OK" && !lower.includes("spam")) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(contactSchema) }}
      />
      <Navbar />
      <main>
        <section className="bg-gradient-to-br from-primary-950 to-primary-800 pt-24 md:pt-28">
          <div className="container-x relative z-10 py-12 md:py-16">
            <nav aria-label="面包屑" className="mb-5 flex items-center gap-2 text-xs text-background-50/80">
              <Link to="/" className="transition-colors hover:text-background-50">
                <i className="ri-home-4-line mr-1"></i>首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50/95">联系我们</span>
            </nav>
            <h1 className="font-heading text-3xl text-background-50 md:text-4xl">联系我们</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-background-50/85 md:text-base">
              想投稿安利、提交避雷建议、反馈问题，或有合作意向？留下你的消息，我们会尽快回复。
            </p>
          </div>
        </section>

        <section className="container-x py-14 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
            {/* 联系信息 */}
            <div className="lg:col-span-2">
              <span className="text-xs font-bold uppercase tracking-widest text-primary-700">联系方式</span>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900">找到我们</h2>
              <div className="mt-6 flex flex-col gap-4">
                {contactItems.map((c) => (
                  <div key={c.title} className="card flex items-start gap-4 p-5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-xl text-primary-700">
                      <i className={c.icon}></i>
                    </span>
                    <div>
                      <h3 className="font-bold text-foreground-900">{c.title}</h3>
                      <p className="mt-1 text-sm text-foreground-700">{c.value}</p>
                      <p className="mt-0.5 text-xs text-foreground-500">{c.hint}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-2xl bg-background-100 p-5">
                <h3 className="flex items-center gap-2 font-bold text-foreground-900">
                  <i className="ri-question-line text-primary-600"></i>
                  常见问题
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-foreground-600">
                  关于求番、避雷、补番顺序的疑问，可以先看看首页的常见问题，大部分答案都在那里。
                </p>
                <Link to="/#faq" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary-700">
                  查看常见问题
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* 表单 */}
            <div className="lg:col-span-3">
              <form
                id="contact-form"
                data-readdy-form="联系我们表单"
                onSubmit={handleSubmit}
                className="card p-6 md:p-8"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-lg text-primary-700">
                    <i className="ri-edit-2-line"></i>
                  </span>
                  <div>
                    <h2 className="font-heading text-xl text-foreground-900">给我们留言</h2>
                    <p className="text-xs text-foreground-500">带 * 号为必填项</p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-bold text-foreground-800">你的称呼 *</span>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="怎么称呼你"
                      className="rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-bold text-foreground-800">邮箱 *</span>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="用于接收回复"
                      className="rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </label>
                </div>

                <label className="mt-4 flex flex-col gap-1.5">
                  <span className="text-sm font-bold text-foreground-800">留言类型</span>
                  <select
                    name="type"
                    className="rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="投稿安利">投稿安利</option>
                    <option value="避雷建议">避雷建议</option>
                    <option value="补番反馈">补番反馈</option>
                    <option value="合作交流">合作交流</option>
                    <option value="其他">其他</option>
                  </select>
                </label>

                <label className="mt-4 flex flex-col gap-1.5">
                  <span className="text-sm font-bold text-foreground-800">留言内容 *</span>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    placeholder="想安利哪部番、想提醒同好避哪个雷，或有什么建议，都欢迎写在这里（最多 500 字）"
                    className="resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm leading-6 text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </label>

                {/* 反垃圾蜜罐字段 */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot-field"
                />

                {status === "success" && (
                  <p className="mt-4 flex items-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm font-bold text-primary-900">
                    <i className="ri-checkbox-circle-fill"></i>
                    提交成功！我们已收到你的消息，会尽快与你联系。
                  </p>
                )}
                {status === "error" && (
                  <p className="mt-4 flex items-center gap-2 rounded-lg bg-secondary-100 px-4 py-3 text-sm font-bold text-secondary-900">
                    <i className="ri-error-warning-fill"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-6 w-full whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === "loading" ? "提交中…" : "提交留言"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}